import { on } from '@ngrx/store';

import { dayjs } from '../../../../core/dayjs/dayjs';

import { DispatchCalendarRemarksDialogActions } from './dispatch-calendar-remarks-dialog.actions';
import { DateRemark, DispatchCalendarRemarksDialogState, dispatchCalendarRemarksDialogInitialState } from './dispatch-calendar-remarks-dialog.state';

function getDateRange(start: string, end: string): string[] {
    const dates: string[] = [];
    let current = dayjs(start).startOf('day');
    const last = dayjs(end).startOf('day');
    while (current.valueOf() <= last.valueOf()) {
        dates.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
    }
    return dates;
}

let localRemarkSeq = 0;

function serverRemarkId(date: string, index: number): string {
    return `${date}#${index}`;
}

function localRemarkId(date: string): string {
    return `${date}#local-${++localRemarkSeq}`;
}

function normalizeSeverity(severity: unknown): DateRemark['severity'] {
    return severity === 'info' || severity === 'success' || severity === 'warn' || severity === 'error'
        ? severity
        : 'info';
};

function sortByDateAndOrder(remarks: DateRemark[]): DateRemark[] {
    return [...remarks].sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
}

function markDirty(dirtyDates: string[], ...dates: string[]): string[] {
    return [...new Set([...dirtyDates, ...dates])];
}

export const dispatchCalendarRemarksDialogReducers = [
    on(
        DispatchCalendarRemarksDialogActions.dialogOpened,
        (): DispatchCalendarRemarksDialogState => ({
            ...dispatchCalendarRemarksDialogInitialState,
            isOpen: true,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.dialogClosed,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            isOpen: false,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.loadViewDatesSuccess,
        (state: DispatchCalendarRemarksDialogState, { viewDates }): DispatchCalendarRemarksDialogState => {
            // Server remarks for each date in the fetched range.
            const serverByDate = new Map<string, DateRemark[]>();
            for (const d of viewDates ?? []) {
                if (d?.date == null) { continue; }
                const remarks = (d.remarks ?? [])
                    .filter((r) => Boolean(r?.text))
                    .map((r, i): DateRemark => ({
                        id: serverRemarkId(d.date, i),
                        date: d.date,
                        text: r!.text,
                        severity: normalizeSeverity(r!.severity),
                    }));
                serverByDate.set(d.date, remarks);
            }

            // Merge: dates present in the server response and NOT locally
            // dirty take the server list; dirty dates and dates outside the
            // response keep their local remarks. This prevents selecting a
            // new date range from wiping unsaved additions.
            const dirtyDates = new Set(state.dirtyDates);
            const merged: DateRemark[] = [];
            for (const [date, serverRemarks] of serverByDate) {
                if (dirtyDates.has(date)) {
                    merged.push(...state.pendingRemarks.filter((r) => r.date === date));
                } else {
                    merged.push(...serverRemarks);
                }
            }
            const serverDates = new Set(serverByDate.keys());
            for (const r of state.pendingRemarks) {
                if (!serverDates.has(r.date)) {
                    merged.push(r);
                }
            }

            return {
                ...state,
                viewDates,
                pendingRemarks: sortByDateAndOrder(merged),
                loadingViewDates: false,
            };
        },
    ),
    on(
        DispatchCalendarRemarksDialogActions.loadViewDatesError,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            loadingViewDates: false,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.datesSelected,
        (state: DispatchCalendarRemarksDialogState, { dates }): DispatchCalendarRemarksDialogState => {
            // Dates arrive as pre-formatted 'YYYY-MM-DD' wall-date strings
            // (see component / initDialogDate effect) so the calendar and
            // any saved remarks agree on the day.
            const validDates = dates?.filter((d): d is string => d != null) ?? [];
            const orderedDates = [...validDates].sort();
            const isCompleteSelection = orderedDates.length > 0 && !(dates?.some((d) => d == null));

            return {
                ...state,
                selectedDates: orderedDates.length ? orderedDates : null,
                loadingViewDates: isCompleteSelection,
            };
        },
    ),
    on(
        DispatchCalendarRemarksDialogActions.remarkTextInputChanged,
        (state: DispatchCalendarRemarksDialogState, { text }): DispatchCalendarRemarksDialogState => ({
            ...state,
            remarkText: text,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.severityDropdownChanged,
        (state: DispatchCalendarRemarksDialogState, { severity }): DispatchCalendarRemarksDialogState => ({
            ...state,
            remarkSeverity: normalizeSeverity(severity),
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.addRemarkButtonClicked,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => {
            const [start, end] = state.selectedDates ?? [];
            if (!start || !state.remarkText.trim()) { return state; }

            // Updating an existing remark in place...
            if (state.editingRemarkId) {
                const editing = state.pendingRemarks.find((r) => r.id === state.editingRemarkId);
                if (!editing) { return state; }
                return {
                    ...state,
                    pendingRemarks: sortByDateAndOrder(state.pendingRemarks.map((r) => r.id === editing.id
                        ? { ...r, text: state.remarkText.trim(), severity: state.remarkSeverity }
                        : r,
                    )),
                    dirtyDates: markDirty(state.dirtyDates, editing.date),
                    remarkText: '',
                    editingRemarkId: null,
                };
            }

            // ...or appending a NEW remark to every date in the selected
            // range. Multiple remarks per date are allowed.
            const dates = getDateRange(start, end ?? start);
            const additions = dates.map((date): DateRemark => ({
                id: localRemarkId(date),
                date,
                text: state.remarkText.trim(),
                severity: state.remarkSeverity,
            }));

            return {
                ...state,
                pendingRemarks: sortByDateAndOrder([...state.pendingRemarks, ...additions]),
                dirtyDates: [...new Set([...state.dirtyDates, ...dates])],
                remarkText: '',
            };
        },
    ),
    on(
        DispatchCalendarRemarksDialogActions.editRemarkButtonClicked,
        (state: DispatchCalendarRemarksDialogState, { remarkId }): DispatchCalendarRemarksDialogState => {
            const remark = state.pendingRemarks.find((r) => r.id === remarkId);
            if (!remark) { return state; }
            return {
                ...state,
                remarkText: remark.text,
                remarkSeverity: remark.severity,
                selectedDates: [remark.date],
                editingRemarkId: remark.id,
            };
        },
    ),
    on(
        DispatchCalendarRemarksDialogActions.editRemarkCancelled,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            remarkText: '',
            editingRemarkId: null,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.deleteRemarkButtonClicked,
        (state: DispatchCalendarRemarksDialogState, { remarkId }): DispatchCalendarRemarksDialogState => {
            const remark = state.pendingRemarks.find((r) => r.id === remarkId);
            if (!remark) { return state; }
            return {
                ...state,
                pendingRemarks: state.pendingRemarks.filter((r) => r.id !== remarkId),
                dirtyDates: markDirty(state.dirtyDates, remark.date),
                editingRemarkId: state.editingRemarkId === remarkId ? null : state.editingRemarkId,
            };
        },
    ),
    on(
        DispatchCalendarRemarksDialogActions.saveButtonClicked,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            saving: true,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.saveRemarksSuccess,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            saving: false,
            // Everything dirty is now persisted — server data is the source
            // of truth again, so a reload can safely replace local state.
            dirtyDates: [],
            editingRemarkId: null,
        }),
    ),
    on(
        DispatchCalendarRemarksDialogActions.saveRemarksError,
        (state: DispatchCalendarRemarksDialogState): DispatchCalendarRemarksDialogState => ({
            ...state,
            saving: false,
        }),
    ),
];